import React from 'react';
import { Building2, CalendarClock, MapPin } from 'lucide-react';
import ReferralStatusBadge from './ReferralStatusBadge';
import ReferralDoctorCard from './ReferralDoctorCard';

const STAGE_LABEL = {
  review_1: 'Review 1',
  review_2: 'Review 2',
  approval: 'Final approval',
  not_required: 'Not required',
  complete: 'Complete',
};

/** Read-only view of the patient-submitted referral request form. */
export default function ReferralSubmittedFormView({ row }) {
  if (!row) return null;
  const p = row.patient || {};
  const s = row.seeking || {};
  const doctor = row.preferred_doctor;
  const stage = STAGE_LABEL[row.fic_stage] || String(row.fic_stage || 'Review 1').replace(/_/g, ' ');

  return (
    <div className="space-y-4 p-5" data-testid="referral-submitted-form">
      <div className="flex flex-wrap items-center gap-2">
        <ReferralStatusBadge status={row.status} />
        <span className="rounded-full border border-stone-200 bg-white px-2.5 py-0.5 text-[11px] font-semibold text-stone-600">
          {row.station_type === 'out_station' ? 'Out-station' : 'In-station'}
        </span>
        <span className="rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-[11px] font-semibold text-amber-900">
          {stage}
        </span>
        {row.needs_credit_letter ? (
          <span className="rounded-full border border-sky-200 bg-sky-50 px-2.5 py-0.5 text-[11px] font-semibold text-sky-900">
            Credit letter requested
          </span>
        ) : null}
      </div>

      <ReferralDoctorCard doctor={doctor} fallbackName={row.preferred_doctor_name} />

      <Section title="Patient" icon={<CalendarClock className="h-4 w-4 text-[#F15A22]" />}>
        <Grid>
          <Line label="Name" value={p.name} />
          <Line label="Relationship" value={p.relationship} />
          <Line label="Phone" value={p.phone} />
          <Line label="Age / Gender" value={`${p.age ?? '—'} / ${p.gender || '—'}`} />
          <Line label="DOB" value={p.dob || '—'} />
          <Line label="MRN" value={p.mrn || p.patient_number || '—'} />
        </Grid>
        <Line label="Address" value={p.address || '—'} multiline />
      </Section>

      <Section title="Seeking referral" icon={<Building2 className="h-4 w-4 text-[#F15A22]" />}>
        <Grid>
          <Line label="Hospital" value={s.hospital_clinic} />
          <Line label="Specialisation" value={s.specialisation} />
          <Line label="City" value={s.city || '—'} />
        </Grid>
        <div className="mt-3 rounded-xl border border-[#E8EEF5] bg-[#F8FAFC] px-3 py-2.5">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-[#7A8B9C]">Reason</p>
          <p className="mt-1 text-sm leading-relaxed text-[#071B34] whitespace-pre-wrap">{s.reason || '—'}</p>
        </div>
      </Section>

      {(row.submitted_at || row.created_at) && (
        <p className="flex items-center gap-1.5 text-[11px] text-[#7A8B9C]">
          <MapPin className="h-3.5 w-3.5" />
          Submitted {fmt(row.submitted_at || row.created_at)}
        </p>
      )}
    </div>
  );
}

function Section({ title, icon, children }) {
  return (
    <section className="rounded-2xl border border-[#D5DEE8] bg-white p-4 shadow-sm">
      <div className="mb-3 flex items-center gap-2">
        {icon}
        <h4 className="font-heading text-base font-semibold text-[#071B34]">{title}</h4>
      </div>
      {children}
    </section>
  );
}

function Grid({ children }) {
  return <div className="grid gap-3 sm:grid-cols-2">{children}</div>;
}

function Line({ label, value, multiline }) {
  return (
    <div className={multiline ? 'mt-3' : ''}>
      <p className="text-[10px] font-semibold uppercase tracking-wide text-[#7A8B9C]">{label}</p>
      <p className={`mt-0.5 text-sm text-[#071B34] ${multiline ? 'whitespace-pre-wrap' : ''}`}>
        {value || '—'}
      </p>
    </div>
  );
}

function fmt(iso) {
  try {
    return new Date(iso).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' });
  } catch {
    return iso;
  }
}
